import { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { SortDirection } from '../../../utils/constants/enums';
import {
  IPagedResult,
  IClubMemberDto,
  IClubMemberApiSearchRequest,
  IFlattenedMemberDisplay
} from '../../../utils/constants/interfaces';
import { IClubMemberLookupUiState } from './ClubMemberLookup';

const emptySearchResults: IPagedResult<IFlattenedMemberDisplay[]> = {
  currentPage: 1,
  results: [],
  pageSize: 20,
  pageCount: 1,
  resultCount: 0
};

const useClubMemberLookup = (
  clubMemberId: string | undefined,
  requireKeywords: boolean,
  onChange: (
    clubMemberId: string,
    searchParams: IClubMemberApiSearchRequest,
    uiState: IClubMemberLookupUiState
  ) => void,
  initialSearchParams?: IClubMemberApiSearchRequest | null,
  initialUiState?: IClubMemberLookupUiState | null,
  getUiState?: () => IClubMemberLookupUiState
) => {
  const defaultSearchParams: IClubMemberApiSearchRequest = {
    quickSearch: '',
    clubMemberId: null,
    legacyMemberId: '',
    lastName: '',
    firstName: '',
    emailAddress: '',
    birthDate: null,
    joinedDateFrom: null,
    joinedDateThru: null,
    firstJoinedFrom: null,
    firstJoinedThru: null,
    profession: '',
    includeSecondaryAdults: true,
    nraMembersOnly: false,
    status: [],
    membershipTypes: []
  };

  const api = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [allResults, setAllResults] = useState<IClubMemberDto[]>([]);
  const [clubMemberPagedResults, setClubMemberPagedResults] =
    useState<IPagedResult<IFlattenedMemberDisplay[]>>(emptySearchResults);
  const [searchParameters, setSearchParameters] = useState<IClubMemberApiSearchRequest>(
    initialSearchParams || defaultSearchParams
  );
  const [sortField, setSortField] = useState<string>('lastname');
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [flattenedResults, setFlattenedResults] = useState<IFlattenedMemberDisplay[]>([]);
  const [hasRestoredSearch, setHasRestoredSearch] = useState<boolean>(false);
  const pageSize = 20;

  // Flatten club member results to show primary and secondary members as separate entries
  // Filter members based on selected status criteria
  const flattenResults = useCallback(
    (clubMembers: IClubMemberDto[], statusFilter: number[]): IFlattenedMemberDisplay[] => {
      const flattened: IFlattenedMemberDisplay[] = [];
      const hasStatusFilter = statusFilter.length > 0;

      clubMembers.forEach((clubMember) => {
        const memberId = parseInt(clubMember.id, 10);

        // Check if primary member matches status filter (or no filter is applied)
        const primaryMatchesStatus =
          !hasStatusFilter ||
          (clubMember.primaryMember.memberStatusId &&
            statusFilter.includes(clubMember.primaryMember.memberStatusId));

        // Add primary member only if it matches the status filter
        if (primaryMatchesStatus) {
          flattened.push({
            clubMemberId: memberId,
            member: clubMember.primaryMember,
            isSecondary: false,
            clubMember
          });
        }

        // Add secondary member if it exists and matches status filter
        if (clubMember.secondaryMember) {
          const secondaryMatchesStatus =
            !hasStatusFilter ||
            (clubMember.secondaryMember.memberStatusId &&
              statusFilter.includes(clubMember.secondaryMember.memberStatusId));

          if (secondaryMatchesStatus) {
            flattened.push({
              clubMemberId: memberId,
              member: clubMember.secondaryMember,
              isSecondary: true,
              clubMember
            });
          }
        }
      });

      return flattened;
    },
    []
  );

  // Handle field updates
  const updateSearchField = <K extends keyof IClubMemberApiSearchRequest>(
    field: K,
    value: IClubMemberApiSearchRequest[K]
  ) => {
    setSearchParameters((prev) => ({ ...prev, [field]: value }));
  };

  // Sort flattened results
  const sortResults = useCallback(
    (results: IFlattenedMemberDisplay[]) => {
      const sorted = [...results].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case 'memberid':
            aValue = Number(a.member.legacyMemberId) || 0;
            bValue = Number(b.member.legacyMemberId) || 0;
            break;
          case 'lastname':
            aValue = a.member.lastName.toLowerCase();
            bValue = b.member.lastName.toLowerCase();
            break;
          case 'firstname':
            aValue = a.member.firstName.toLowerCase();
            bValue = b.member.firstName.toLowerCase();
            break;
          case 'middlename':
            aValue = (a.member.middleName || '').toLowerCase();
            bValue = (b.member.middleName || '').toLowerCase();
            break;
          case 'email':
            aValue = a.member.emailAddress.toLowerCase();
            bValue = b.member.emailAddress.toLowerCase();
            break;
          case 'dob':
            aValue = a.member.dateOfBirthUtc ? new Date(a.member.dateOfBirthUtc).getTime() : 0;
            bValue = b.member.dateOfBirthUtc ? new Date(b.member.dateOfBirthUtc).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (sortDirection === SortDirection.ASCENDING) {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      return sorted;
    },
    [sortField, sortDirection]
  );

  // Apply pagination to flattened results
  const paginateResults = useCallback(
    (results: IFlattenedMemberDisplay[]) => {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedResults = results.slice(startIndex, endIndex);
      const pageCount = Math.ceil(results.length / pageSize);

      setClubMemberPagedResults({
        currentPage,
        results: paginatedResults,
        pageSize,
        pageCount,
        resultCount: results.length
      });
    },
    [currentPage]
  );

  // Update displayed results when sort or page changes
  useEffect(() => {
    if (flattenedResults.length > 0) {
      const sorted = sortResults(flattenedResults);
      paginateResults(sorted);
    }
  }, [flattenedResults, sortField, sortDirection, currentPage, sortResults, paginateResults]);

  const search = async (): Promise<void> => {
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.quickSearch)) {
      return;
    }

    setLoading(true);
    setCurrentPage(1); // Reset to first page on new search

    // Build request body to match C# ClubMemberSearchRequest object (camelCase for JSON)
    const requestBody = {
      quickSearch: searchParameters.quickSearch,
      clubMemberId: searchParameters.clubMemberId,
      legacyMemberId: searchParameters.legacyMemberId,
      firstName: searchParameters.firstName,
      lastName: searchParameters.lastName,
      emailAddress: searchParameters.emailAddress,
      birthDate: searchParameters.birthDate,
      joinedDateFrom: searchParameters.joinedDateFrom,
      joinedDateThru: searchParameters.joinedDateThru,
      includeSecondaryAdults: searchParameters.includeSecondaryAdults,
      firstJoinedFrom: searchParameters.firstJoinedFrom,
      firstJoinedThru: searchParameters.firstJoinedThru,
      profession: searchParameters.profession,
      nraMembersOnly: searchParameters.nraMembersOnly,
      status: searchParameters.status,
      membershipTypes: searchParameters.membershipTypes
    };

    const endpoint = `/members/search`;
    const apiResponse = await api.post<IClubMemberDto[]>(endpoint, JSON.stringify(requestBody));

    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      setAllResults(apiResponse.response);
      // Pass the status filter to filter members by their individual status
      const flattened = flattenResults(apiResponse.response, searchParameters.status);
      setFlattenedResults(flattened);
      const sorted = sortResults(flattened);
      paginateResults(sorted);
    } else {
      setAllResults([]);
      setFlattenedResults([]);
      setClubMemberPagedResults(emptySearchResults);
    }
    setLoading(false);
  };

  const resetSearch = () => {
    setSearchParameters(defaultSearchParams);
    setAllResults([]);
    setFlattenedResults([]);
    setClubMemberPagedResults(emptySearchResults);
    setCurrentPage(1);
  };

  // Run search automatically if initial search params were provided (restoring from navigation)
  useEffect(() => {
    if (initialSearchParams && !hasRestoredSearch) {
      setHasRestoredSearch(true);
      void search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchParams, hasRestoredSearch]);

  const changePage = (page: number) => {
    setCurrentPage(page);
  };

  const changeSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(
        sortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
      );
    } else {
      setSortField(field);
      setSortDirection(SortDirection.ASCENDING);
    }
  };

  const selectClubMember = (clubMemberId: string) => {
    setShowSearch(false);
    // Pass current search parameters and UI state to onChange so they can be preserved
    const uiState = getUiState?.() ?? { showFilters: false, showAdvancedOptions: false };
    onChange(clubMemberId, searchParameters, uiState);
  };

  const hasSearchParametersCheck = () => {
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.quickSearch)) return false;

    return (
      !Helpers.isNullOrWhiteSpace(searchParameters.quickSearch) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.firstName) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.lastName) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.emailAddress) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.legacyMemberId) ||
      searchParameters.clubMemberId !== null ||
      searchParameters.status.length > 0 ||
      searchParameters.membershipTypes.length > 0
    );
  };

  return {
    loading,
    searchParameters,
    clubMemberPagedResults,
    flattenedResults,
    updateSearchField,
    showSearch,
    hasSearchParameters: hasSearchParametersCheck(),
    setShowSearch,
    selectClubMember,
    changePage,
    changeSort,
    sortField,
    sortDirection,
    search,
    resetSearch
  };
};

export default useClubMemberLookup;
